import type { ThrottleHealth } from '../../types/throttle';
import { formatEpochMsAgo } from '../../utils/throttle';
import { cn } from '../../utils/cn';

interface ThrottleHealthBadgeProps {
  health: ThrottleHealth | null;
  className?: string;
}

const STATE_STYLES: Record<string, { classes: string; label: string }> = {
  awaiting_first_poll: {
    classes: 'badge-purple',
    label: 'Starting',
  },
  healthy: {
    classes: 'badge-success',
    label: 'Healthy',
  },
  stale: {
    classes: 'badge-warning',
    label: 'Stale',
  },
  failing: {
    classes: 'badge-danger',
    label: 'Failing',
  },
};

const UNKNOWN_STYLE = {
  classes: 'badge-neutral',
  label: 'Unknown',
};

/** Status pill for the Throttle API data pipeline (GET /v1/health `state`). */
export const ThrottleHealthBadge = ({ health, className }: ThrottleHealthBadgeProps) => {
  const style = (health && STATE_STYLES[health.state]) || UNKNOWN_STYLE;

  const tooltipParts: string[] = [];
  if (health?.lastSuccessAt) {
    tooltipParts.push(`Last successful poll ${formatEpochMsAgo(health.lastSuccessAt)}`);
  }
  if (health && health.consecutiveFailures > 0) {
    tooltipParts.push(`${health.consecutiveFailures} consecutive poll failure(s)`);
  }
  if (health?.lastError) {
    tooltipParts.push(`Last error: ${health.lastError}`);
  }
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join('\n') : 'Throttle data pipeline status';

  return (
    <span
      title={tooltip}
      className={cn(
        'badge cursor-default',
        style.classes,
        className,
      )}
    >
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full shrink-0',
          health?.state === 'healthy' && 'bg-green-400',
          health?.state === 'stale' && 'bg-yellow-400',
          health?.state === 'failing' && 'bg-red-400 animate-pulse',
          health?.state === 'awaiting_first_poll' && 'bg-purple-400 animate-pulse',
          !health && 'bg-gray-400',
        )}
      />
      {style.label}
    </span>
  );
};
